import { Assignation } from '../../context/types'
import _ from 'lodash'
import { getStatements } from '../../i18n/Statements'
import { info, logException } from '../../services/sentryLogger'
import { buckets2str, rounds2str, stms2str } from '../../utils/stm'

const LOG_TAG = 'BUCKET_TS'

export const REF_GAUSSIAN = [2, 4, 5, 6, 8, 6, 5, 4, 2] // FIXME minus deckmode multiple
export let REF_GAUSSIAN_STR = ''
REF_GAUSSIAN.forEach((n, index) => {
    const val = String(index + 1)
    REF_GAUSSIAN_STR += `${val.repeat(n)}`
})

/**
 * Assign Bucket
 * Enrich with other values
 * @param prevAssignations
 * @param sortedArray
 * @returns
 */
export const sortAndExtend = (
    prevAssignations: readonly Assignation[],
    sortedArray: readonly Assignation[], // last is best
    extendNumber: 0 | 1 | 2 | 3 = 0,
    langIndex: number
) => {
    const newAssignations: Assignation[] = []
    sortedArray.forEach((a, i) => {
        if (!a) {
            // FIXME requis ?
            logException(
                LOG_TAG,
                'sortAndExtend',
                new Error('undefined assignation')
            )
            return
        }
        i = sortedArray.length - 1 - i // reverse order FIXME
        const { category, index } = a
        const baseIndex = i * extendNumber
        newAssignations.push({
            ...a,
            index: baseIndex + i + 1,
            weight: prevAssignations[index]?.weight, // FIXME
            bucket: i + 1, // FIXME in assignBuckets()
        })
        if (!extendNumber) {
            return
        }
        // extension
        const Statements = getStatements(langIndex)
        Statements.filter((s) => s.category === category && s.id !== a.id)
            ?.slice(0, extendNumber)
            ?.forEach((s, j) => {
                newAssignations.push({
                    ...(s as Assignation),
                    index: baseIndex + 1 + j,
                    weight: 0, // FIXME
                    bucket: i + 1, // FIXME in assignBuckets()
                })
            })
    })
    info(
        LOG_TAG,
        'sortAndExtend',
        extendNumber,
        'newAssignations',
        stms2str(newAssignations)
    )
    return newAssignations
}

export const assignBuckets = (
    sortedArray: Assignation[],
    nBuckets: number,
    autoMode: boolean = true
) => {
    info(
        LOG_TAG,
        'assignBuckets',
        {
            nBuckets,
            autoMode,
        },
        'sortedArray',
        stms2str(sortedArray)
    )
    let distro = []
    const reversed = [...sortedArray].reverse() // FIXME check if needed
    if (!autoMode && nBuckets === 9) {
        logException(
            'assignBuckets',
            'map2Buckets: 9 buckets but not 27 values - autoMode false but 9 buckets'
        )
        // gaussian distro
        distro = map2BucketsCustom(reversed.length, nBuckets)
    } else {
        // constant distro
        distro = Array(nBuckets).fill(sortedArray.length / nBuckets)
    }
    return sortedArray
}

// FIXME only work for 9 buckets
function map2BucketsCustom(
    numberOfItems: number,
    numberOfBins: number = 9
): number[] {
    // Custom distribution pattern based on the example provided
    const pattern = REF_GAUSSIAN.slice(0)

    // Adjust the pattern to fit the number of bins and items
    const scale_factor = numberOfItems / pattern.reduce((a, b) => a + b, 0)
    let bins = pattern.map((p) => Math.round(p * scale_factor))

    // Adjust for rounding errors
    let total = bins.reduce((a, b) => a + b, 0)
    let index = 0
    while (total < numberOfItems) {
        bins[index % numberOfBins]++
        total++
        index++
    }

    return bins
}

export const pickRandom = (n: number, as: Assignation[]): Assignation[] =>
    _.shuffle(as).slice(n)

export type SinglePicker = (
    type: 'BEST' | 'WORST',
    assignations: Assignation[],
) => Promise<number>

// FIXME update progress !
export const partialBucketFromRounds = async (
    rounds: Assignation[][],
    pickStm: SinglePicker
) => {
    const buckets: Assignation[][] = Array(REF_GAUSSIAN.length)
        .fill(null)
        .map(() => [])
    const nBucket = REF_GAUSSIAN.length
    const middleBucket = 4
    const maxRank = rounds[0].length
    info(
        'partialBucketFromRounds',
        {
            maxRank,
            nBucket,
            middleBucket,
        },
        rounds2str(rounds)
    )
    const asgnsForRanksOriginal = Array(maxRank)
        .fill(0)
        .map((_, rank) => {
            // info(LOG_TAG, 'reduce', { rank, rounds})
            let output: Assignation[] = []
            output = rounds.reduce((acc, round) => {
                const item = round[rank]
                // info(LOG_TAG, 'reduce', {
                //     acc,
                //     round,
                //     item,
                //     rounds, // FIXME round 3 = copy of round 1, leakage
                //     rank,
                // })
                const newAcc = [...acc, item]
                // FIXME
                // info(LOG_TAG, 'reduce', { newAcc })
                return newAcc
            }, output)
            info(LOG_TAG, 'map', { rank }, 'output', stms2str(output))
            return output
        })
    info(LOG_TAG, 'asgnsForRanksOriginal', stms2str(asgnsForRanksOriginal))
    const asgnsForRanks = asgnsForRanksOriginal.slice(0).map((a) => a.slice(0))
    let currBucket = 0
    info(
        LOG_TAG,
        {
            maxRank,
            middleBucket,
        },
        rounds2str(rounds),
        'asgnsForRanks',
        stms2str(asgnsForRanks)
    )

    let currRankAsc = 0
    let asgnsForRank: Assignation[] = []
    while (currRankAsc < maxRank && currBucket < middleBucket) {
        info(
            `===== BUCKET ${currBucket}/ ${nBucket - 1} =======`,
            `RANK: ${currRankAsc}/ ${maxRank - 1}`
        )

        asgnsForRank = asgnsForRanks.splice(currRankAsc, 1, [])[0]
        let currentBucketMax = REF_GAUSSIAN[currBucket]
        let currentBucketSize = buckets[currBucket]?.length
        const overflow =
            currentBucketSize + asgnsForRank.length - currentBucketMax
        info(
            LOG_TAG,
            'asc',
            {
                currBucket,
                overflow,
                currentBucketMax,
                currentBucketSize,
            },
            'asgnsForRank',
            stms2str(asgnsForRank),
            'buckets',
            buckets2str(buckets)
        )
        if (overflow <= 0) {
            asgnsForRank.forEach((a) => (a.bucket = currBucket + 1))
            buckets[currBucket].push(...asgnsForRank)
            asgnsForRank.splice(0, asgnsForRank.length)
            currRankAsc++
            if (overflow === 0) {
                currBucket++
            }
        } else if (overflow === 1) {
            const a2moveIndex = await pickStm('WORST', asgnsForRank)
            const a2move = asgnsForRank.splice(a2moveIndex, 1)[0]
            asgnsForRank.forEach((a) => (a.bucket = currBucket + 1))
            buckets[currBucket].push(...asgnsForRank)
            asgnsForRank.splice(0, asgnsForRank.length)
            currBucket++
            a2move.bucket = currBucket + 1
            buckets[currBucket].push(a2move)
        } else if (overflow % 3 === 0) {
            currBucket++
        } else {
            const msg = `overflow ${overflow} not expected for rank ${currRankAsc}`
            logException(
                'partialBucketFromRounds',
                msg,
                new Error(
                    JSON.stringify(
                        { asgnsForRank, currBucket, overflow },
                        null,
                        ' '
                    )
                )
            )
            throw new Error(msg)
        }
        info(LOG_TAG, 'asc iter end', { currRankAsc }, buckets2str(buckets))
    }

    // restore
    // asgnsForRanks.splice(currRankAsc, 0, asgnsForRank)

    // go again

    // FIXME
    // info(
    //     LOG_TAG,
    //     'asc end',
    //     JSON.stringify(
    //         { asgnsForRank, currRankAsc, currBucket, asgnsForRanks },
    //         null,
    //         ' '
    //     )
    // )
    let currRankDesc = maxRank - 1
    currBucket = nBucket - 1
    while (currRankDesc > 0 && currBucket > middleBucket) {
        info(
            `===== BUCKET ${currBucket}/ ${nBucket - 1} =======`,
            `RANK: ${currRankDesc}/ ${maxRank - 1}`
        )
        const asgnsForRank = asgnsForRanks.splice(currRankDesc, 1, [])[0]
        // info(JSON.stringify({ asgnsForRank, currRankAsc }, null, ' '))

        let currentBucketMax = REF_GAUSSIAN[currBucket]
        let currentBucketSize = buckets[currBucket]?.length
        const overflow =
            currentBucketSize + asgnsForRank.length - currentBucketMax
        // info({ overflow })
        if (overflow <= 0) {
            asgnsForRank.forEach((a) => (a.bucket = currBucket + 1))
            buckets[currBucket].push(...asgnsForRank)
            asgnsForRank.splice(0, asgnsForRank.length)
            currRankDesc--
            if (overflow === 0) {
                currBucket--
            }
        } else if (overflow === 1) {
            const a2moveIndex = await pickStm('BEST', asgnsForRank)
            const a2move = asgnsForRank.splice(a2moveIndex, 1)[0]
            asgnsForRank.forEach((a) => (a.bucket = currBucket + 1))
            buckets[currBucket].push(...asgnsForRank)
            asgnsForRank.splice(0, asgnsForRank.length)
            currBucket--
            a2move.bucket = currBucket + 1
            buckets[currBucket].push(a2move)
        } else if (overflow % 3 === 0) {
            currBucket--
        } else {
            const msg = `overflow ${overflow} not expected for rank ${currRankDesc}`
            logException(
                'partialBucketFromRounds',
                msg,
                new Error(
                    JSON.stringify(
                        {
                            asgnsForRank,
                            currBucket,
                            overflow,
                        },
                        null,
                        ' '
                    )
                )
            )
            throw new Error(msg)
        }
        info(
            'end loop',
            { overflow, currRankAsc },
            buckets2str(buckets),
            'asgnsForRank',
            stms2str(asgnsForRank)
        )
    }
    info(
        LOG_TAG,
        'END END mid bucket',
        buckets2str(buckets),
        'asgnsForRanks',
        stms2str(asgnsForRanks)
    )
    const remainingAsgns = _.flattenDeep(asgnsForRanks)
    remainingAsgns.forEach((a) => (a.bucket = middleBucket + 1))
    buckets[middleBucket].push(...remainingAsgns)
    info(
        LOG_TAG,
        'final',
        buckets2str(buckets),
        'remainingAsgns',
        stms2str(remainingAsgns)
    )
    return buckets
}

export const invertBuckets = 'ok';